/**
 * Copyright (c) 2022, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
 * You may not alter or remove any copyright or other notice from copies of this content.
 */

import { SIGN_OUT_SUCCESS_PARAM, STATE } from "@asgardeo/auth-react";
import axios, { AxiosResponse } from "axios";
import React, { FunctionComponent, ReactElement, useEffect, useState } from "react";
import { AppWithClientIdentifier, GlobalLoader } from "./components";
import {
    APP_BASE_URL_STORAGE_KEY,
    APP_CLIENT_ID_STORAGE_KEY,
    APP_CLIENT_ID_TEMP_STORAGE_KEY,
    APP_CLIENT_ID_URL_SEARCH_PARAM_KEY,
    APP_STATE_STORAGE_KEY,
    USERS_ORGANIZATION_STORAGE_KEY,
    USERS_ORGANIZATION_TEMP_STORAGE_KEY,
    USERS_ORGANIZATION_URL_SEARCH_PARAM_KEY
} from "./constants";
import { useConfig } from "./hooks";
import { LandingPage, SignedOutPage } from "./pages";

/**
 * Main App component.
 *
 * @returns ReactElement
 */
export const App: FunctionComponent = (): ReactElement => {

    const clientId: string = new URL(window.location.href).searchParams
        .get(APP_CLIENT_ID_URL_SEARCH_PARAM_KEY) as string;
    const organization: string = new URL(window.location.href).searchParams
        .get(USERS_ORGANIZATION_URL_SEARCH_PARAM_KEY) as string;

    const { deployment } = useConfig();

    const [
        isClientIdInStorage,
        setIsClientIdInStorage
    ] = useState<boolean>(!!window.sessionStorage.getItem(APP_CLIENT_ID_STORAGE_KEY));

    const [
        isBaseUrlInStorage,
        setIsBaseUrlInStorage
    ] = useState<boolean>(!!window.sessionStorage.getItem(APP_BASE_URL_STORAGE_KEY));

    const [ isLoggedOut, setIsLoggedOut ] = useState(false);

    /**
     * Hook to update the session storage when the `clientId` and the `org` is available in the URL params.
     */
    useEffect(() => {
        if (!clientId || !organization) {
            const state: string = new URL(window.location.href).searchParams.get(STATE) as string;

            window.sessionStorage.setItem(APP_STATE_STORAGE_KEY, state);
            if (window.sessionStorage.
                getItem(APP_STATE_STORAGE_KEY) as string === SIGN_OUT_SUCCESS_PARAM){
                setIsLoggedOut(true);
            }

            return;
        }

        window.sessionStorage.setItem(APP_CLIENT_ID_STORAGE_KEY, clientId);
        window.sessionStorage.setItem(USERS_ORGANIZATION_STORAGE_KEY, organization);
        window.sessionStorage.removeItem(APP_CLIENT_ID_TEMP_STORAGE_KEY);
        window.sessionStorage.removeItem(USERS_ORGANIZATION_TEMP_STORAGE_KEY);
    }, [ clientId, organization ]);

    /**
     * Hook to form the base url.
     */
    useEffect(() => {
        if (deployment && organization) {
            const wellKnownConfigUrl: string =
                `${ deployment.serverOrigin }/${ deployment.tenantPrefix }/${ organization }` 
                + "/oauth2/token/.well-known/openid-configuration";

            // Make an HTTP GET request to the well-known configuration URL.
            axios.get(wellKnownConfigUrl)
                .then((response: AxiosResponse) => {
                    const authorizationRequestEndpoint: string = response.data.authorization_endpoint;
                    const url: string = authorizationRequestEndpoint.split("/oauth2")[0];

                    window.sessionStorage.setItem(APP_BASE_URL_STORAGE_KEY, url);
                    setIsBaseUrlInStorage(true);
                })
                .catch(() => {
                    // Catch any error that occurred while fetching the well-known configuration.
                });
        }
    }, [ deployment, organization ]);

    /**
     * Hook to update the internal state when the clientId in the storage changes.
     */
    useEffect(() => {
        setIsClientIdInStorage(!!window.sessionStorage.getItem(APP_CLIENT_ID_STORAGE_KEY));
    }, [ isClientIdInStorage ]);

    if (!deployment || !isBaseUrlInStorage) {
        return <GlobalLoader data-componentid="app-root-global-loader" />;
    }

    return (
        isClientIdInStorage
            ? (
                <AppWithClientIdentifier
                    clientId={ window.sessionStorage.getItem(APP_CLIENT_ID_STORAGE_KEY) as string }
                    tenant={ window.sessionStorage.getItem(USERS_ORGANIZATION_STORAGE_KEY) as string }
                    baseUrl={ window.sessionStorage.getItem(APP_BASE_URL_STORAGE_KEY) as string }
                />
            )
            : isLoggedOut
                ? <SignedOutPage data-componentid="signed-out-page" />
                : <LandingPage data-componentid="landing-page" />
    );
};
